// Eased 0→1 progress for count-up numbers (the blend page's ledger animates in).
// Mirrors the design doc's easing: cubic ease-out over ~1.3s, one rAF pass,
// plays once. Jumps straight to 1 when animations are suppressed (test/reduced
// motion) so screenshots and a11y stay stable.

import { useEffect, useRef, useState } from "react";
import { IS_TEST } from "./env.ts";

// The count-up is a brief, non-vestibular flourish, so it plays even under
// reduce-motion (only continuous motion is suppressed, in CSS). Test mode still
// jumps to the final value for deterministic screenshots.
function motionOff(): boolean {
  return IS_TEST;
}

/** Returns progress 0→1 that eases in once on mount. */
export function useCountUpProgress(durationMs = 1300): number {
  const [p, setP] = useState(() => (motionOff() ? 1 : 0));
  const raf = useRef<number | null>(null);
  useEffect(() => {
    if (motionOff()) return;
    let start: number | null = null;
    const tick = (now: number) => {
      if (start == null) start = now;
      const t = Math.min(1, (now - start) / durationMs);
      setP(1 - Math.pow(1 - t, 3)); // easeOutCubic
      if (t < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => {
      if (raf.current != null) cancelAnimationFrame(raf.current);
    };
  }, [durationMs]);
  return p;
}
